
import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useStore } from '../hooks/useStore.ts';
import { api } from '../services/api.ts';
import { formatCurrency, calculateFinancialMetrics } from '../services/financialService.ts';
import { useToast } from '../hooks/useToast.ts';
import { Modal } from '../components/Modal.tsx';
import { DocumentsPanel } from '../components/DocumentsPanel.tsx';
import { FinancialsSection } from '../components/project-detail/FinancialsSection.tsx';
import { ComplianceSection } from '../components/project-detail/ComplianceSection.tsx';
import { ReportsSection } from '../components/project-detail/ReportsSection.tsx';
import { BudgetPanel } from '../components/BudgetPanel.tsx';
import { ChangeOrdersPanel } from '../components/ChangeOrdersPanel.tsx';
import { SubcontractorsPanel } from '../components/SubcontractorsPanel.tsx';
import { ScheduleView } from '../components/ScheduleView.tsx';
import { LogsPanel } from '../components/LogsPanel.tsx';
import { AuditLedger } from '../components/AuditLedger.tsx';
import { CollaborationBar } from '../components/CollaborationBar.tsx';
import { Loader2, ArrowLeft, Settings, HardHat, MapPin, Archive, CalendarDays, Receipt, Gavel, Sparkles, ShieldCheck, Activity, Target, AlertTriangle, TrendingDown } from 'lucide-react';
import { generateProjectBrief } from '../services/geminiService.ts';

export const ProjectDetail: React.FC = () => {
   const { id } = useParams<{ id: string }>();
   const projectId = Number(id);
   const navigate = useNavigate();
   const { addToast } = useToast();

   const project = useStore(() => api.projects.get(projectId), [projectId], 'projects');
   const evm = useStore(() => api.evm.list(e => e.projectId === projectId), [projectId], 'evm');
   const risks = useStore(() => api.risks.list(r => r.projectId === projectId), [projectId], 'risks');
   const compliance = useStore(() => api.complianceRequirements.list(c => c.projectId === projectId), [projectId], 'complianceRequirements');
   const changeOrders = useStore(() => api.changeOrders.list(co => co.projectId === projectId.toString()), [projectId], 'changeOrders') || [];

   const [tab, setTab] = useState<string>('evm');
   const [modal, setModal] = useState<string | null>(null);

   const [brief, setBrief] = useState<{ narrative: string; healthScore: number; keyTakeaway: string; marginRisk: string } | null>(null);
   const [loadingBrief, setLoadingBrief] = useState(false);

   const revisedContractAmount = useMemo(() => {
      if (!project) return 0;
      const coTotal = changeOrders.filter(co => co.status === 'Approved').reduce((acc, co) => acc + co.amount, 0);
      return project.originalContractValue + coTotal;
   }, [project, changeOrders]);

   const lastMetrics = useMemo(() => {
      if (!project || !evm || evm.length === 0) return null;
      const last = [...evm].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
      return calculateFinancialMetrics(last, project.originalContractValue, changeOrders);
   }, [project, evm, changeOrders]);

   useEffect(() => {
      if (project && evm && risks && compliance && !brief && !loadingBrief) {
         loadBrief();
      }
   }, [project, evm, risks, compliance]);

   const loadBrief = async () => {
      if (!project) return;
      setLoadingBrief(true);
      try {
         const res = await generateProjectBrief(project, lastMetrics, risks || [], compliance || []);
         setBrief(res);
      } catch (err) {
         console.error("Failed to load brief", err);
      } finally {
         setLoadingBrief(false);
      }
   };

   const handleArchive = async () => {
      if (!project) return;
      navigate('/projects');
      await api.projects.delete(projectId);
      addToast({ type: 'info', title: 'Archived', message: `"${project.title}" has been moved to archives.` });
   };

   if (!project) return <div className="flex h-screen items-center justify-center bg-zinc-50"><Loader2 className="animate-spin text-zinc-300" size={32} /></div>;

   return (
      <div className="flex flex-col h-screen overflow-hidden bg-zinc-50">
         <CollaborationBar />

         <div className="flex-1 overflow-auto custom-scrollbar">
            <div className="max-w-7xl mx-auto space-y-10 p-8 md:p-12 pb-40 animate-in fade-in duration-700 print:p-0">
               <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 print:hidden">
                  <div className="space-y-4">
                     <Link to="/projects" className="inline-flex items-center text-[9px] font-black text-zinc-400 uppercase tracking-widest hover:text-black transition-colors">
                        <ArrowLeft size={12} className="mr-2" /> Portfolio Registry
                     </Link>
                     <div className="flex items-center gap-6">
                        <h1 className="text-4xl font-black text-black tracking-tighter leading-none uppercase">
                           {project.title}
                        </h1>
                        <span className={`text-[9px] uppercase px-3 py-1 rounded font-black border tracking-widest ${project.status === 'Active' ? 'bg-black text-white border-black' : 'bg-white border-zinc-200 text-zinc-500 shadow-sm'
                           }`}>{project.status}</span>
                     </div>

                     <div className="flex flex-wrap gap-x-12 gap-y-4 pt-2">
                        <div className="flex items-center gap-3 text-xs text-zinc-500 font-bold uppercase tracking-widest"><HardHat size={16} className="text-zinc-300" /> {project.client}</div>
                        <div className="flex items-center gap-3 text-xs text-zinc-500 font-bold uppercase tracking-widest"><MapPin size={16} className="text-zinc-300" /> {project.location}</div>
                        <div className="flex items-center gap-3 text-xs text-zinc-500 font-bold uppercase tracking-widest"><CalendarDays size={16} className="text-zinc-300" /> {new Date(project.startDate).toLocaleDateString()} — {new Date(project.endDate).toLocaleDateString()}</div>
                        <div className="flex flex-col">
                           <span className="text-[8px] font-black uppercase text-zinc-400 tracking-widest mb-1">Revised Magnitude</span>
                           <div className="text-black font-black text-xl tracking-tight flex items-center gap-2">
                              <Receipt size={16} className="text-zinc-300" />
                              {formatCurrency(revisedContractAmount)}
                           </div>
                        </div>
                     </div>
                  </div>
                  <div className="flex items-center gap-3">
                     <button onClick={loadBrief} className="p-4 bg-white border border-zinc-100 rounded-xl hover:bg-zinc-50 text-zinc-300 shadow-sm transition-all" title="Refresh Intelligence">
                        {loadingBrief ? <Loader2 size={18} className="animate-spin text-black" /> : <Activity size={18} className="text-black" />}
                     </button>
                     <button onClick={() => setModal('settings')} className="p-4 bg-white border border-zinc-100 rounded-xl hover:bg-zinc-50 text-zinc-300 shadow-sm transition-all"><Settings size={18} /></button>
                  </div>
               </div>

               {/* AI EXECUTIVE BRIEF SECTION (MARGIN PROTECTION FOCUSED) */}
               <div className="bg-white rounded-[3.5rem] border border-zinc-200 p-10 shadow-sm overflow-hidden relative group">
                  <div className="absolute top-0 right-0 p-10 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none">
                     <Sparkles size={160} />
                  </div>

                  {loadingBrief ? (
                     <div className="py-12 flex flex-col items-center justify-center space-y-4">
                        <Loader2 size={32} className="animate-spin text-zinc-200" />
                        <p className="text-[10px] font-black uppercase tracking-widest text-zinc-300">Auditing Project Baseline Intelligence...</p>
                     </div>
                  ) : brief ? (
                     <div className="space-y-8 animate-in zoom-in-95 duration-500">
                        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                           <div className="flex items-center gap-4">
                              <div className="p-3 bg-black rounded-2xl text-white shadow-xl shadow-black/10">
                                 <ShieldCheck size={24} className="text-emerald-400" />
                              </div>
                              <div>
                                 <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-400">Command Brief</h4>
                                 <div className="text-xl font-black text-black uppercase tracking-tight mt-1">Margin Defense Status</div>
                              </div>
                           </div>
                           <div className="flex flex-wrap gap-4">
                              <div className="px-6 py-3 bg-zinc-50 rounded-2xl border border-zinc-100 flex items-center gap-4">
                                 <span className="text-[8px] font-black uppercase text-zinc-400 tracking-widest">Health Index</span>
                                 <span className={`text-sm font-black ${brief.healthScore > 80 ? 'text-emerald-600' : brief.healthScore > 50 ? 'text-amber-500' : 'text-rose-600'}`}>
                                    {brief.healthScore}%
                                 </span>
                              </div>
                              <div className="px-6 py-3 bg-black rounded-2xl flex items-center gap-4 text-white">
                                 <Target size={14} className="text-amber-400" />
                                 <span className="text-[8px] font-black uppercase tracking-[0.2em]">{brief.keyTakeaway}</span>
                              </div>
                              {brief.marginRisk && (
                                 <div className="px-6 py-3 bg-rose-50 rounded-2xl border border-rose-100 flex items-center gap-4 text-rose-600 animate-pulse">
                                    <AlertTriangle size={14} />
                                    <span className="text-[8px] font-black uppercase tracking-[0.2em]">{brief.marginRisk}</span>
                                 </div>
                              )}
                           </div>
                        </div>

                        <div className="pt-8 border-t border-zinc-50">
                           <p className="text-xl font-medium text-zinc-700 leading-relaxed italic max-w-5xl">
                              "{brief.narrative}"
                           </p>
                        </div>
                     </div>
                  ) : (
                     <div className="py-12 text-center">
                        <p className="text-[10px] font-black uppercase tracking-widest text-zinc-300">Insufficient operational logs for AI synthesis.</p>
                     </div>
                  )}
               </div>

               <div className="flex gap-2 p-1.5 bg-zinc-100/50 rounded-2xl w-fit border border-zinc-100 overflow-x-auto no-scrollbar print:hidden">
                  {[
                     { id: 'evm', label: 'EVM Metrics' },
                     { id: 'logs', label: 'Forensic site logs' },
                     { id: 'subcontractors', label: 'Partners' },
                     { id: 'wbs', label: 'WBS schedule' },
                     { id: 'changeorders', label: 'Claims' },
                     { id: 'risks', label: 'Compliance' },
                     { id: 'documents', label: 'Assets' },
                     { id: 'reports', label: 'Synthesis' },
                     { id: 'audit', label: 'Audit Trail', icon: <Gavel size={10} /> }
                  ].map(t =>
                     <button key={t.id} onClick={() => setTab(t.id)} className={`px-6 py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all whitespace-nowrap flex items-center gap-2 ${tab === t.id ? 'bg-black text-white shadow-lg' : 'text-zinc-400 hover:text-black'}`}>
                        {t.icon} {t.label}
                     </button>
                  )}
               </div>

               <div className="py-4">
                  {tab === 'evm' && <FinancialsSection project={project} />}
                  {tab === 'logs' && <LogsPanel projectId={projectId} />}
                  {tab === 'subcontractors' && <SubcontractorsPanel projectId={projectId} />}
                  {tab === 'wbs' && <ScheduleView projectId={projectId} />}
                  {tab === 'changeorders' && <ChangeOrdersPanel projectId={projectId} />}
                  {tab === 'risks' && <ComplianceSection projectId={projectId} />}
                  {tab === 'documents' && <DocumentsPanel projectId={projectId} />}
                  {tab === 'reports' && <ReportsSection project={project} />}
                  {tab === 'audit' && <AuditLedger projectId={projectId} />}
               </div>

               <Modal isOpen={!!modal} onClose={() => setModal(null)} title="Build Controls" maxWidth="sm">
                  <div className="space-y-4">
                     <button onClick={handleArchive} className="w-full bg-rose-50 text-rose-600 font-black uppercase tracking-widest py-4 rounded-xl transition-all hover:bg-rose-100 flex items-center justify-center gap-2 text-xs">
                        <Archive size={16} /> Move to Archives
                     </button>
                  </div>
               </Modal>
            </div>
         </div>
      </div>
   );
};
